import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * DevIdInspector — 로컬/개발 환경 전용 UI 요소 id 인스펙터.
 *
 * 트리거:
 *   1. capture-once 모드 (외부 CustomEvent 'dev-id-inspector:capture-once' 로 on/off)
 *      - id 있는 요소 전부 오렌지 얇은 dashed 라운드 outline (박스 두께 최소)
 *      - 전체 화면을 덮는 클릭 잡기 layer 가 실제 click 을 대신 흡수 → 라우팅/네비게이션 발생 없음
 *      - elementFromPoint 로 아래 요소 찾아 가장 가까운 id 복사 + CustomEvent 'dev-id-inspector:captured' { id } 발사
 *      - 클릭 후 자동 off (연속 수집이 필요하면 다시 켜기)
 *      - pill: 위치 최상위, drag handle 로 이동
 *   2. Alt+Click 백업 — 모드 무관, Alt 누른 채 클릭 시 즉시 id 복사
 *
 * z-index 두 layer:
 *   - outline layer: FeedbackPanel(2147483646) 보다 아래 (2147483640) — Panel 이 outline 위에 뜸
 *   - pill/flash layer: Panel 보다 위 (2147483648)
 */

const isEnabledEnv = (): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (import.meta as any).env;
    if (!env) return false;
    if (env.DEV === true) return true;
    if (env.VITE_APP_ENV === 'local' || env.VITE_APP_ENV === 'dev') return true;
    return false;
  } catch {
    return false;
  }
};

const CAPTURE_ONCE_EVENT = 'dev-id-inspector:capture-once';
const CAPTURED_EVENT = 'dev-id-inspector:captured';
const PILL_POS_KEY = 'dev-id-inspector-pill-pos';

interface OutlineRect {
  id: string;
  top: number;
  left: number;
  width: number;
  height: number;
}

function findElementWithId(el: Element | null): Element | null {
  let cur: Element | null = el;
  while (cur && cur !== document.body) {
    if (cur.id) return cur;
    cur = cur.parentElement;
  }
  return null;
}

function collectIdRects(): OutlineRect[] {
  if (typeof document === 'undefined') return [];
  const nodes = document.querySelectorAll<HTMLElement>('body [id]');
  const list: OutlineRect[] = [];
  nodes.forEach((el) => {
    if (el.closest('[data-dev-id-inspector]')) return;
    if (el.closest('[data-dev-id-inspector-outlines]')) return;
    if (el.closest('[data-dev-feedback-launcher]')) return;
    if (el.closest('[data-dev-feedback-panel]')) return;
    const r = el.getBoundingClientRect();
    if (r.width < 8 || r.height < 8) return;
    if (r.bottom < 0 || r.top > window.innerHeight) return;
    if (r.right < 0 || r.left > window.innerWidth) return;
    list.push({ id: el.id, top: r.top, left: r.left, width: r.width, height: r.height });
  });
  return list;
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export const DevIdInspector: React.FC = () => {
  if (!isEnabledEnv()) return null;

  const [captureOnce, setCaptureOnce] = useState<boolean>(false);
  const [outlineRects, setOutlineRects] = useState<OutlineRect[]>([]);
  const [flashCopied, setFlashCopied] = useState<string | null>(null);
  const [pillPos, setPillPos] = useState<{ x: number; y: number }>(() => {
    try {
      const raw = localStorage.getItem(PILL_POS_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    return { x: -1, y: 60 };
  });
  const pillRef = useRef<HTMLDivElement | null>(null);
  const outlineLayerRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef<{ ox: number; oy: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    try { localStorage.setItem(PILL_POS_KEY, JSON.stringify(pillPos)); } catch { /* noop */ }
  }, [pillPos]);

  // 외부에서 capture-once 토글
  useEffect(() => {
    const onToggle = (e: Event) => {
      const detail = (e as CustomEvent<{ on?: boolean }>).detail;
      if (detail && typeof detail.on === 'boolean') setCaptureOnce(detail.on);
      else setCaptureOnce((v) => !v);
    };
    window.addEventListener(CAPTURE_ONCE_EVENT, onToggle);
    return () => window.removeEventListener(CAPTURE_ONCE_EVENT, onToggle);
  }, []);

  // capture-once outline 계산
  useEffect(() => {
    if (!captureOnce) { setOutlineRects([]); return; }
    const recompute = () => setOutlineRects(collectIdRects());
    recompute();
    const throttled = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(recompute);
    };
    window.addEventListener('scroll', throttled, true);
    window.addEventListener('resize', throttled);
    const t = window.setInterval(recompute, 800);
    return () => {
      window.removeEventListener('scroll', throttled, true);
      window.removeEventListener('resize', throttled);
      clearInterval(t);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [captureOnce]);

  // Alt+Click 백업 트리거 (모드 무관, 항상)
  useEffect(() => {
    const onAltClick = async (e: MouseEvent) => {
      if (!e.altKey) return;
      const target = e.target as Element | null;
      if (!target) return;
      if (target.closest('[data-dev-id-inspector]')) return;
      if (target.closest('[data-dev-id-inspector-outlines]')) return;
      const withId = findElementWithId(target);
      if (!withId?.id) return;
      e.preventDefault();
      e.stopPropagation();
      const ok = await copyToClipboard(`#${withId.id}`);
      setFlashCopied(ok ? `복사됨: #${withId.id}` : `복사 실패 — 직접 복사: #${withId.id}`);
      setTimeout(() => setFlashCopied(null), ok ? 2000 : 4000);
      window.dispatchEvent(new CustomEvent(CAPTURED_EVENT, { detail: { id: withId.id } }));
    };
    window.addEventListener('click', onAltClick, true);
    return () => window.removeEventListener('click', onAltClick, true);
  }, []);

  // outline layer 자체가 클릭을 잡아 실제 element 에 click 이 안 가도록 방어
  const onLayerCapture = useCallback(async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const layer = outlineLayerRef.current;
    if (!layer) return;
    // 자기 자신을 잠시 hit-test 에서 제외해 아래 요소를 찾음
    const prev = layer.style.pointerEvents;
    layer.style.pointerEvents = 'none';
    const under = document.elementFromPoint(e.clientX, e.clientY);
    layer.style.pointerEvents = prev;
    if (!under) return;
    // Panel 안이면 스킵 (Panel 은 layer 위 z-index 라 원래 여기로 안 오지만 안전망)
    if (under.closest('[data-dev-feedback-panel]')) return;
    if (under.closest('[data-dev-feedback-launcher]')) return;
    if (under.closest('[data-dev-id-inspector]')) return;
    const withId = findElementWithId(under);
    if (!withId?.id) {
      // id 없는 곳 클릭 → capture-once 유지, 아무 동작 없음
      return;
    }
    // 클립보드 실패 (permission/focus) 여도 textarea 삽입은 되어야 함 → dispatch 는 항상.
    const ok = await copyToClipboard(`#${withId.id}`);
    setFlashCopied(ok ? `복사됨: #${withId.id}` : `복사 실패 — 직접 복사: #${withId.id}`);
    setTimeout(() => setFlashCopied(null), ok ? 2000 : 4000);
    window.dispatchEvent(new CustomEvent(CAPTURED_EVENT, { detail: { id: withId.id } }));
    setCaptureOnce(false);
    // Chat 등 외부 UI 가 버튼 라벨을 다시 'off' 로 되돌리도록 브로드캐스트
    window.dispatchEvent(new CustomEvent(CAPTURE_ONCE_EVENT, { detail: { on: false } }));
  }, []);

  // Pill drag (mouse + touch)
  useEffect(() => {
    const mv = (e: MouseEvent | TouchEvent) => {
      if (!dragging.current) return;
      const p = 'touches' in e ? e.touches[0] : e;
      if (!p) return;
      setPillPos({
        x: Math.max(0, Math.min(window.innerWidth - 40, p.clientX - dragging.current.ox)),
        y: Math.max(0, Math.min(window.innerHeight - 24, p.clientY - dragging.current.oy)),
      });
    };
    const up = () => { dragging.current = null; };
    window.addEventListener('mousemove', mv);
    window.addEventListener('touchmove', mv, { passive: false });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', mv);
      window.removeEventListener('touchmove', mv);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, []);

  const onDragHandleStart = (e: React.MouseEvent | React.TouchEvent) => {
    const rect = pillRef.current?.getBoundingClientRect();
    const startX = rect?.left ?? pillPos.x;
    const startY = rect?.top ?? pillPos.y;
    const p = 'touches' in e ? e.touches[0] : e;
    dragging.current = { ox: p.clientX - startX, oy: p.clientY - startY };
    e.preventDefault();
  };

  return (
    <>
      {/* Outline layer — Panel 보다 낮은 z-index. 클릭을 대신 흡수해 실제 요소 라우팅 차단 */}
      {captureOnce && (
        <div
          data-dev-id-inspector-outlines
          ref={outlineLayerRef}
          onClick={onLayerCapture}
          onMouseDownCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onMouseUpCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onPointerDownCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onPointerUpCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
          onTouchStartCapture={(e) => { e.preventDefault(); e.stopPropagation(); }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2147483640,
            pointerEvents: 'auto',
            cursor: 'crosshair',
            background: 'rgba(0,0,0,0.02)',
          }}
        >
          {outlineRects.map((r) => (
            <div
              key={`${r.id}-${r.top}-${r.left}`}
              style={{
                position: 'fixed',
                top: r.top - 1,
                left: r.left - 1,
                width: r.width + 2,
                height: r.height + 2,
                border: '1.5px dashed #e94d20',
                borderRadius: 6,
                background: 'rgba(233,77,32,0.04)',
                pointerEvents: 'none',
              }}
            />
          ))}
        </div>
      )}

      {/* Pill/flash layer — Panel 위 z-index (항시 최상단) */}
      <div
        data-dev-id-inspector
        style={{ position: 'fixed', zIndex: 2147483648, pointerEvents: 'none', inset: 0 }}
      >
        {captureOnce && (
          <div
            ref={pillRef}
            style={{
              position: 'fixed',
              top: pillPos.y,
              left: pillPos.x >= 0 ? pillPos.x : '50%',
              transform: pillPos.x >= 0 ? undefined : 'translateX(-50%)',
              background: 'rgba(233,77,32,0.95)',
              color: 'white',
              padding: '6px 10px 6px 6px',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              pointerEvents: 'auto',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              userSelect: 'none',
            }}
          >
            <span
              title="드래그해서 이동"
              onMouseDown={onDragHandleStart}
              onTouchStart={onDragHandleStart}
              style={{
                cursor: 'move',
                padding: '2px 4px',
                fontSize: 14,
                lineHeight: 1,
                color: 'rgba(255,255,255,0.85)',
                letterSpacing: -2,
              }}
            >
              ⋮⋮
            </span>
            <span>🎯 id 수집중 — 요소 탭하면 복사·피드백 삽입</span>
            <button
              onClick={() => {
                setCaptureOnce(false);
                window.dispatchEvent(new CustomEvent(CAPTURE_ONCE_EVENT, { detail: { on: false } }));
              }}
              style={{
                background: 'white',
                color: '#e94d20',
                border: 'none',
                borderRadius: 999,
                padding: '3px 10px',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              off
            </button>
          </div>
        )}

        {flashCopied && (
          <div
            style={{
              position: 'fixed',
              bottom: 24,
              right: 16,
              background: '#2E7D32',
              color: 'white',
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 12,
              fontFamily: 'ui-monospace, monospace',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              pointerEvents: 'none',
            }}
          >
            {flashCopied}
          </div>
        )}
      </div>
    </>
  );
};
